import React from 'react';
import { motion } from 'framer-motion';
import { CheckCircleIcon } from '@heroicons/react/24/solid';
import Analytics from '../../services/analytics';

interface PricingFeature {
  text: string;
  included: boolean;
}

interface PricingCardProps {
  name: string;
  price: string;
  description: string;
  features: PricingFeature[];
  popularPlan?: boolean;
  buttonText?: string;
  delay?: number;
  onSelect?: (planName: string) => void;
}

const PricingCard: React.FC<PricingCardProps> = ({
  name,
  price,
  description,
  features,
  popularPlan = false,
  buttonText = 'Get Started',
  delay = 0,
  onSelect,
}) => {
  return (
    <motion.div
      className={`card h-full flex flex-col ${
        popularPlan ? 'border-2 border-primary-500' : ''
      }`}
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{
        duration: 0.5,
        delay: delay,
        type: "spring",
        stiffness: 100
      }}
      whileHover={{ 
        y: -5,
        boxShadow: "0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)"
      }}
    >
      {popularPlan && (
        <div className="bg-primary-500 text-white py-1 px-4 rounded-t-xl text-center font-medium">
          Most Popular
        </div>
      )}
      <div className="p-6 flex-grow flex flex-col">
        <h3 className="text-xl font-bold mb-2">{name}</h3>
        <div className="mt-2 mb-4">
          <span className="text-4xl font-bold">{price}</span>
          {price !== 'Custom' && <span className="text-gray-500">/month</span>}
        </div>
        <p className="text-gray-600 mb-6">{description}</p>
        <ul className="space-y-3 mb-8">
          {features.map((feature, index) => (
            <li key={index} className="flex items-start">
              <CheckCircleIcon 
                className={`h-5 w-5 mr-2 flex-shrink-0 ${
                  feature.included ? 'text-primary-500' : 'text-gray-300'
                }`} 
              />
              <span className={feature.included ? 'text-gray-700' : 'text-gray-400'}>
                {feature.text}
              </span>
            </li>
          ))}
        </ul>
        <motion.button
          className={`mt-auto w-full py-3 rounded-lg font-medium ${
            popularPlan
              ? 'bg-primary-600 text-white hover:bg-primary-700'
              : 'bg-gray-100 text-gray-800 hover:bg-gray-200'
          }`}
          whileHover={{ scale: 1.03 }}
          whileTap={{ scale: 0.98 }}
          onClick={() => {
            Analytics.trackPricingInteraction(name.toLowerCase(), 'select');
            if (onSelect) onSelect(name);
          }}
        >
          {buttonText}
        </motion.button>
      </div>
    </motion.div>
  );
};

export default PricingCard;